import { Container, Graphics } from 'pixi.js';
import { AUDIO_KEYS, AUDIO_PATHS, type AudioKey } from '../assets/manifest';
import { COLORS, lighten } from '../ui/theme';

const MUTED_STORAGE_KEY = 'nanocars.muted';

const MAX_ENGINE_VOICES = 6;
const ENGINE_RATE_MIN = 0.6;
const ENGINE_RATE_MAX = 1.6;
const ENGINE_GAIN = 0.45;
// Distance (metres, world space - same units as car/camera positions) at
// which an engine has faded to silent. Roughly half a typical viewW, so a
// car near the camera's centre reads full volume and one near the screen
// edge is nearly gone.
const ENGINE_FALLOFF_M = 12;

const SKID_THROTTLE_S = 0.3;
const SKID_KEYS: readonly AudioKey[] = ['skid_1', 'skid_2', 'skid_3'];
const BUMP_KEYS: readonly AudioKey[] = ['bump_1', 'bump_2', 'bump_3'];

const MUSIC_LOBBY_GAIN = 0.35;
const MUSIC_DUCK_GAIN = 0.15;
const MUSIC_FADE_S = 0.4;

function readMuted(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(MUTED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeMuted(value: boolean): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(MUTED_STORAGE_KEY, value ? '1' : '0');
  } catch {
    // Storage can throw (private browsing, quota); losing persistence isn't fatal.
  }
}

function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

interface EngineVoice {
  source: AudioBufferSourceNode;
  gain: GainNode;
}

// Thin Web Audio wrapper (no library, no dependency on pixi's sound
// ecosystem). Static/singleton - there is exactly one audio device for the
// whole app, same shape as state/Session.ts's `session`.
//
// Every voice (engines, one-shots, the music loop) routes through a single
// master GainNode, so mute is one gain flip rather than tracking every live
// source. `ctx`/`master` stay null until init() successfully creates a
// context (or forever, if AudioContext doesn't exist - e.g. under
// happy-dom/vitest, or a headless screenshot run that never fires a user
// gesture), and every public method below checks that before touching
// anything, so calling any of them before init(), or in an environment with
// no Web Audio, is always a safe no-op - never a throw.
export class Audio {
  private static ctx: AudioContext | null = null;
  private static master: GainNode | null = null;
  private static gestureBound = false;
  private static muted = readMuted();

  private static buffers = new Map<AudioKey, AudioBuffer>();
  private static loadPromise: Promise<void> | null = null;

  private static engineVoices = new Map<number, EngineVoice>();
  private static lastSkidAt = new Map<number, number>();

  private static musicSource: AudioBufferSourceNode | null = null;
  private static musicGain: GainNode | null = null;

  // Idempotent: safe to call from every scene's enter(). Creates the
  // AudioContext (if the platform has one) and arms it to resume on the
  // first pointerdown/keydown anywhere in the document - browsers refuse to
  // run an AudioContext until a user gesture, so playback before that point
  // is silent (not an error) until the resume fires. Also wires the M key
  // as a global mute toggle (skipped while an <input>/<textarea> has focus,
  // so it doesn't fight the name field on Title).
  static init(): void {
    Audio.ensureContext();
    if (Audio.gestureBound || typeof window === 'undefined') return;
    Audio.gestureBound = true;

    const resume = (): void => {
      void Audio.ensureContext()?.resume();
    };
    window.addEventListener('pointerdown', resume);
    window.addEventListener('keydown', resume);

    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'm' || e.key === 'M') Audio.toggleMute();
    });
  }

  private static ensureContext(): AudioContext | null {
    if (Audio.ctx) return Audio.ctx;
    const w = globalThis as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
    const Ctor = w.AudioContext ?? w.webkitAudioContext;
    if (!Ctor) return null;
    try {
      const ctx = new Ctor();
      const master = ctx.createGain();
      master.gain.value = Audio.muted ? 0 : 1;
      master.connect(ctx.destination);
      Audio.ctx = ctx;
      Audio.master = master;
      return ctx;
    } catch {
      return null;
    }
  }

  // Fetches and decodes every manifest clip once; safe to call repeatedly
  // (from every scene that might be entered first) - later calls just await
  // the same in-flight/settled promise. A missing/corrupt clip is dropped
  // from the cache rather than rejecting the whole load, since every
  // playback helper below already no-ops on a missing buffer.
  static async loadAll(): Promise<void> {
    if (Audio.loadPromise) return Audio.loadPromise;
    const ctx = Audio.ensureContext();
    if (!ctx) return;

    Audio.loadPromise = (async () => {
      await Promise.all(
        AUDIO_KEYS.map(async (key) => {
          try {
            const res = await fetch(AUDIO_PATHS[key]);
            const data = await res.arrayBuffer();
            const buf = await ctx.decodeAudioData(data);
            Audio.buffers.set(key, buf);
          } catch {
            // Leave it out of the cache; playback for this key stays a no-op.
          }
        }),
      );
    })();
    return Audio.loadPromise;
  }

  static get isMuted(): boolean {
    return Audio.muted;
  }

  static setMuted(value: boolean): void {
    Audio.muted = value;
    writeMuted(value);
    if (Audio.master) Audio.master.gain.value = value ? 0 : 1;
  }

  static toggleMute(): void {
    Audio.setMuted(!Audio.muted);
  }

  // -- music loop -----------------------------------------------------------

  static readonly music = {
    /** Starts the looping music_loop bed at MUSIC_LOBBY_GAIN. No-op if it's already playing. */
    play(): void {
      Audio.startMusic();
    },
    stop(): void {
      Audio.stopMusic();
    },
    /** Ramps the music bed's gain over `rampS` seconds (e.g. ducking for a race, or back up for results). */
    setGain(gain: number, rampS = MUSIC_FADE_S): void {
      Audio.setMusicGain(gain, rampS);
    },
  } as const;

  private static startMusic(): void {
    const ctx = Audio.ctx;
    if (!ctx || !Audio.master || Audio.musicSource) return;
    const buf = Audio.buffers.get('music_loop');
    if (!buf) return;

    const source = ctx.createBufferSource();
    source.buffer = buf;
    source.loop = true;
    const gain = ctx.createGain();
    gain.gain.value = MUSIC_LOBBY_GAIN;
    source.connect(gain);
    gain.connect(Audio.master);
    source.start();

    Audio.musicSource = source;
    Audio.musicGain = gain;
  }

  private static stopMusic(): void {
    if (!Audio.musicSource) return;
    try {
      Audio.musicSource.stop();
    } catch {
      // Already stopped.
    }
    Audio.musicSource.disconnect();
    Audio.musicGain?.disconnect();
    Audio.musicSource = null;
    Audio.musicGain = null;
  }

  private static setMusicGain(gain: number, rampS: number): void {
    const ctx = Audio.ctx;
    const node = Audio.musicGain;
    if (!ctx || !node) return;
    const target = Math.max(0, gain);
    if (rampS <= 0) {
      node.gain.value = target;
      return;
    }
    node.gain.cancelScheduledValues(ctx.currentTime);
    node.gain.setValueAtTime(node.gain.value, ctx.currentTime);
    node.gain.linearRampToValueAtTime(target, ctx.currentTime + rampS);
  }

  // -- per-car engine loop ----------------------------------------------------

  // Maintains one looping engine_loop source per car (up to MAX_ENGINE_VOICES
  // - a new carId beyond that is silently ignored). Call every render frame
  // for every car that should currently be heard; playbackRate maps
  // speedFrac (0..1) to ENGINE_RATE_MIN..ENGINE_RATE_MAX, and gain falls off
  // linearly to 0 at ENGINE_FALLOFF_M metres from the camera.
  static engine(carId: number, speedFrac: number, distanceToCamera: number): void {
    const ctx = Audio.ctx;
    if (!ctx || !Audio.master) return;

    let voice = Audio.engineVoices.get(carId);
    if (!voice) {
      if (Audio.engineVoices.size >= MAX_ENGINE_VOICES) return;
      const buf = Audio.buffers.get('engine_loop');
      if (!buf) return;

      const source = ctx.createBufferSource();
      source.buffer = buf;
      source.loop = true;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      source.connect(gain);
      gain.connect(Audio.master);
      source.start();

      voice = { source, gain };
      Audio.engineVoices.set(carId, voice);
    }

    const frac = clamp01(speedFrac);
    voice.source.playbackRate.value = ENGINE_RATE_MIN + (ENGINE_RATE_MAX - ENGINE_RATE_MIN) * frac;
    const distGain = clamp01(1 - distanceToCamera / ENGINE_FALLOFF_M);
    voice.gain.gain.value = ENGINE_GAIN * distGain;
  }

  static stopEngine(carId: number): void {
    const voice = Audio.engineVoices.get(carId);
    if (!voice) return;
    try {
      voice.source.stop();
    } catch {
      // Already stopped.
    }
    voice.source.disconnect();
    voice.gain.disconnect();
    Audio.engineVoices.delete(carId);
  }

  /** Stops any engine voice whose carId isn't in `activeIds` - call once per render frame after updating every currently-heard car. */
  static stopStaleEngines(activeIds: ReadonlySet<number>): void {
    for (const id of Array.from(Audio.engineVoices.keys())) {
      if (!activeIds.has(id)) Audio.stopEngine(id);
    }
  }

  static stopAllEngines(): void {
    for (const id of Array.from(Audio.engineVoices.keys())) Audio.stopEngine(id);
  }

  // -- one-shots --------------------------------------------------------------

  private static playOneShot(key: AudioKey, gain = 1): void {
    const ctx = Audio.ctx;
    const buf = Audio.buffers.get(key);
    if (!ctx || !Audio.master || !buf) return;

    const source = ctx.createBufferSource();
    source.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = gain;
    source.connect(g);
    g.connect(Audio.master);
    source.start();
    source.onended = () => {
      source.disconnect();
      g.disconnect();
    };
  }

  /** Throttled to one per SKID_THROTTLE_S per car, so a sustained drift doesn't spam the clip. */
  static skid(carId: number): void {
    if (!Audio.ctx) return;
    const now = performance.now() / 1000;
    const last = Audio.lastSkidAt.get(carId) ?? -Infinity;
    if (now - last < SKID_THROTTLE_S) return;
    Audio.lastSkidAt.set(carId, now);
    Audio.playOneShot(pick(SKID_KEYS));
  }

  static bump(): void {
    Audio.playOneShot(pick(BUMP_KEYS));
  }

  static fall(): void {
    Audio.playOneShot('fall');
  }

  static beep(): void {
    Audio.playOneShot('beep');
  }

  static go(): void {
    Audio.playOneShot('go');
  }

  static finish(): void {
    Audio.playOneShot('finish');
  }
}

export { MUSIC_LOBBY_GAIN, MUSIC_DUCK_GAIN };

// -- mute button widget -------------------------------------------------------

const MUTE_BUTTON_SIZE = 40;
const MUTE_BUTTON_HOVER_ALPHA = 0.85;

// Small speaker icon button, drawn entirely with Graphics (no image asset,
// no emoji glyph): a speaker cone plus either sound-wave arcs (unmuted) or a
// slash through the cone (muted). Hover changes the panel's alpha only - no
// positional "lift", matching the rest of the house button style
// (ui/widgets.ts). Meant to be placed top-right of a scene's container.
export function createMuteButton(): Container {
  const container = new Container();
  const bg = new Graphics();
  const icon = new Graphics();
  container.addChild(bg, icon);
  container.eventMode = 'static';
  container.cursor = 'pointer';

  const redraw = (): void => {
    bg.clear().roundRect(0, 0, MUTE_BUTTON_SIZE, MUTE_BUTTON_SIZE, 10).fill(COLORS.panel);
    bg.stroke({ width: 2, color: COLORS.outline });
    drawSpeakerIcon(icon, MUTE_BUTTON_SIZE / 2, MUTE_BUTTON_SIZE / 2, Audio.isMuted, COLORS.textDark);
  };
  redraw();

  container.on('pointerover', () => {
    bg.alpha = MUTE_BUTTON_HOVER_ALPHA;
  });
  container.on('pointerout', () => {
    bg.alpha = 1;
  });
  container.on('pointertap', () => {
    Audio.toggleMute();
    redraw();
  });

  return container;
}

function drawSpeakerIcon(g: Graphics, cx: number, cy: number, muted: boolean, color: number): void {
  g.clear();

  const coneX = cx - 12;
  const bodyW = 7;
  const bodyH = 9;

  // Speaker body: a small rectangle plus a triangular horn, both centred
  // vertically on cy.
  g.rect(coneX, cy - bodyH / 2, bodyW, bodyH).fill(color);
  g.poly([
    coneX + bodyW,
    cy - bodyH / 2,
    coneX + bodyW,
    cy + bodyH / 2,
    coneX + bodyW + 8,
    cy + bodyH / 2 + 6,
    coneX + bodyW + 8,
    cy - bodyH / 2 - 6,
  ]).fill(color);

  if (muted) {
    g.moveTo(cx - 4, cy - 12).lineTo(cx + 14, cy + 12).stroke({ width: 3, color });
  } else {
    g.circle(coneX + bodyW + 8, cy, 8).stroke({ width: 2, color });
    g.circle(coneX + bodyW + 8, cy, 13).stroke({ width: 2, color, alpha: 0.6 });
  }
}
